'use client';

import { useState } from 'react';
import { Settings2 } from 'lucide-react';
import type { ContactFormBlock, ContactFormField, ContactFormFieldType } from '@impact/types';
import { Button } from '@/components/ui/button/button';
import { Input } from '@/components/ui/input/input';
import { Label } from '@/components/ui/label/label';
import { Switch } from '@/components/ui/switch/switch';
import {
  Select,
  SelectContent,
  SelectItem as SI,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select/select';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover/popover';
import { ContactFormLive } from './_form/ContactFormLive';
import { usePageEditorStore } from '@/components/page-editor/v2/store/usePageEditor';
import { setPath } from '@/components/page-editor/v2/lib/setPath';
import { cn } from '@/lib/utils';

interface ContactFormBlockRendererProps {
  block: ContactFormBlock;
  editing?: boolean;
}

const FIELD_TYPE_OPTIONS: { value: ContactFormFieldType; label: string }[] = [
  { value: 'text', label: 'Short text' },
  { value: 'textarea', label: 'Long text' },
  { value: 'email', label: 'Email' },
  { value: 'phone', label: 'Phone' },
  { value: 'url', label: 'URL' },
  { value: 'number', label: 'Number' },
  { value: 'date', label: 'Date' },
  { value: 'time', label: 'Time' },
  { value: 'select', label: 'Dropdown' },
  { value: 'radio', label: 'Radio buttons' },
  { value: 'checkbox', label: 'Checkbox' },
];

export function ContactFormBlockRenderer({ block, editing }: ContactFormBlockRendererProps) {
  if (!editing) return <ContactFormLive block={block} />;
  return <ContactFormPreview block={block} />;
}

function ContactFormPreview({ block }: { block: ContactFormBlock }) {
  const { title, subtitle, fields, submitLabel = 'Send message' } = block.data;

  return (
    <div className="my-12">
      <h2
        className="inline-block min-w-[6rem] text-2xl md:text-3xl font-bold text-foreground mb-2 empty:before:text-muted-foreground/50 empty:before:content-['Form_title']"
        data-editable="data.title"
      >
        {title}
      </h2>
      <p
        className="inline-block min-w-[6rem] text-muted-foreground mb-8 empty:before:text-muted-foreground/50 empty:before:content-['Subtitle']"
        data-editable="data.subtitle"
      >
        {subtitle}
      </p>

      <div className="space-y-5 max-w-xl">
        {fields.length === 0 ? (
          <p className="text-sm text-muted-foreground border border-dashed rounded-md p-4 text-center">
            No fields yet — use the “+ Add field” button below.
          </p>
        ) : (
          fields.map((field, i) => (
            <FieldPreview key={field.id} blockId={block.id} field={field} index={i} />
          ))
        )}
        <button
          type="button"
          disabled
          className={cn(
            'w-full px-6 py-3 rounded-lg bg-primary text-primary-foreground font-medium opacity-90',
          )}
        >
          <span data-editable="data.submitLabel">{submitLabel}</span>
        </button>
      </div>
    </div>
  );
}

function FieldPreview({
  blockId,
  field,
  index,
}: {
  blockId: string;
  field: ContactFormField;
  index: number;
}) {
  const labelEl = (
    <span
      className="inline-block min-w-[4rem] empty:before:text-muted-foreground/50 empty:before:content-['Field_label']"
      data-editable={`data.fields[${index}].label`}
    >
      {field.label}
    </span>
  );

  const placeholderEditableEl = (
    <span
      className="inline-block min-w-[4rem] empty:before:text-muted-foreground/50 empty:before:content-['Placeholder']"
      data-editable={`data.fields[${index}].placeholder`}
    >
      {field.placeholder}
    </span>
  );

  const inputShell = (
    <div className="w-full px-4 py-2.5 rounded-lg border border-input bg-background text-sm text-muted-foreground">
      {placeholderEditableEl}
    </div>
  );

  const renderField = () => {
    switch (field.type) {
      case 'textarea':
        return (
          <div className="w-full px-4 py-2.5 rounded-lg border border-input bg-background text-sm text-muted-foreground min-h-[6rem]">
            {placeholderEditableEl}
          </div>
        );
      case 'select':
        return (
          <div className="w-full px-4 py-2.5 rounded-lg border border-input bg-background text-sm text-muted-foreground flex items-center justify-between">
            <span
              className="inline-block min-w-[4rem] empty:before:text-muted-foreground/50 empty:before:content-['Select_an_option']"
              data-editable={`data.fields[${index}].placeholder`}
            >
              {field.placeholder}
            </span>
            <span className="text-muted-foreground/60">▾</span>
          </div>
        );
      case 'radio':
        return (
          <div className="space-y-2">
            {(field.options ?? ['Option 1', 'Option 2']).map((opt, j) => (
              <label key={j} className="flex items-center gap-2 text-sm text-muted-foreground">
                <input type="radio" disabled className="h-4 w-4" />
                <span>{opt}</span>
              </label>
            ))}
          </div>
        );
      case 'checkbox':
        return (
          <label className="flex items-center gap-3">
            <input type="checkbox" disabled className="h-4 w-4 rounded border-input" />
            <span
              className="text-sm text-muted-foreground inline-block min-w-[4rem] empty:before:text-muted-foreground/50 empty:before:content-['Checkbox_label']"
              data-editable={`data.fields[${index}].placeholder`}
            >
              {field.placeholder}
            </span>
          </label>
        );
      case 'date':
      case 'time':
      case 'number':
        return inputShell;
      case 'email':
      case 'phone':
      case 'url':
      case 'text':
      default:
        return inputShell;
    }
  };

  const isCheckbox = field.type === 'checkbox';

  return (
    <div data-block-item={String(index)} className="relative group">
      <FieldSettingsButton blockId={blockId} field={field} index={index} />
      {!isCheckbox && (
        <label className="block text-sm font-medium text-foreground mb-1.5">
          {labelEl}
          {field.required && <span className="text-red-500 ml-1">*</span>}
          <span className="ml-2 text-xs font-normal text-muted-foreground/70 uppercase tracking-wide">
            {fieldTypeLabel(field.type)}
          </span>
        </label>
      )}
      {renderField()}
      {field.helpText ? (
        <p
          className="mt-1 text-xs text-muted-foreground inline-block min-w-[4rem] empty:before:text-muted-foreground/50 empty:before:content-['Help_text']"
          data-editable={`data.fields[${index}].helpText`}
        >
          {field.helpText}
        </p>
      ) : null}
    </div>
  );
}

function fieldTypeLabel(type: ContactFormFieldType): string {
  return FIELD_TYPE_OPTIONS.find((o) => o.value === type)?.label ?? type;
}

function FieldSettingsButton({
  blockId,
  field,
  index,
}: {
  blockId: string;
  field: ContactFormField;
  index: number;
}) {
  const [open, setOpen] = useState(false);
  const block = usePageEditorStore((s) => s.blocks.find((b) => b.id === blockId));
  const updateBlock = usePageEditorStore((s) => s.updateBlock);
  const updateBlockPath = usePageEditorStore((s) => s.updateBlockPath);

  const setFieldProp = <K extends keyof ContactFormField>(key: K, value: ContactFormField[K]) => {
    updateBlockPath(blockId, `data.fields[${index}].${key}`, value);
  };

  const setOptions = (text: string) => {
    const opts = text
      .split('\n')
      .map((s) => s.trim())
      .filter(Boolean);
    if (!block) return;
    const next = setPath(block, `data.fields[${index}].options`, opts);
    updateBlock(blockId, next);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label="Field settings"
          className="absolute -right-9 top-0 z-10 inline-flex h-7 w-7 items-center justify-center rounded-md border bg-background text-muted-foreground opacity-0 shadow-sm transition-opacity hover:text-foreground group-hover:opacity-100"
          onClick={(e) => e.stopPropagation()}
        >
          <Settings2 className="h-3.5 w-3.5" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        className="w-72 space-y-3 p-3"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="space-y-1">
          <Label className="text-xs">Field type</Label>
          <Select
            value={field.type}
            onValueChange={(v) => setFieldProp('type', v as ContactFormFieldType)}
          >
            <SelectTrigger className="h-8 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {FIELD_TYPE_OPTIONS.map((o) => (
                <SI key={o.value} value={o.value}>
                  {o.label}
                </SI>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="flex items-center justify-between">
          <Label className="text-xs">Required</Label>
          <Switch
            checked={Boolean(field.required)}
            onCheckedChange={(v) => setFieldProp('required', v)}
          />
        </div>

        {(field.type === 'select' || field.type === 'radio') && (
          <div className="space-y-1">
            <Label className="text-xs">Options (one per line)</Label>
            <textarea
              defaultValue={(field.options ?? []).join('\n')}
              onBlur={(e) => setOptions(e.target.value)}
              className="w-full min-h-[5rem] rounded-md border border-input bg-background px-2 py-1.5 text-xs"
              placeholder="Option 1&#10;Option 2"
            />
          </div>
        )}

        {field.type === 'number' && (
          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-1">
              <Label className="text-xs">Min</Label>
              <Input
                type="number"
                value={field.min ?? ''}
                onChange={(e) =>
                  setFieldProp('min', e.target.value === '' ? undefined : Number(e.target.value))
                }
                className="h-8 text-xs"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Max</Label>
              <Input
                type="number"
                value={field.max ?? ''}
                onChange={(e) =>
                  setFieldProp('max', e.target.value === '' ? undefined : Number(e.target.value))
                }
                className="h-8 text-xs"
              />
            </div>
          </div>
        )}

        <div className="space-y-1">
          <Label className="text-xs">Help text</Label>
          <Input
            value={field.helpText ?? ''}
            onChange={(e) => setFieldProp('helpText', e.target.value)}
            placeholder="Optional helper shown below the field"
            className="h-8 text-xs"
          />
        </div>
      </PopoverContent>
    </Popover>
  );
}
